import cloneDeep from 'lodash.clonedeep';
import { forkJoin } from 'rxjs';
import { map, mergeMap, retry, shareReplay } from 'rxjs/operators';
import { BOTH_DEFAULT_AND_LEGACY_DATA, DEFAULT_ONLY_DATA, LEGACY_ONLY_DATA, } from 'AccountsApp/ts/constants/user-preferences.constants';
import { EVESTMENT_DATA_FEATURE, LEGACY_DATA_FEATURE, UCITS_DATA_FEATURE, } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.constants';
import { COMPANY, DOMICILE_ALL, DOMICILE_GENERAL, DOMICILE_NASDAQ, DOMICILE_UCITS, FUND, INDEX, INDICATOR, MODEL_PORTFOLIO, SECURITYLIST_GROUP_MULTI, } from 'NewAngular/constants/core.constants';
import { SECURITYLIST_MENU_ORDER_MAP } from 'SecurityListsApp/ts/constants';
import { AccountsService } from 'AccountsApp/ts/services/accounts.service';
import { FeatureService } from 'EntitlementsApp/ts/features/feature-needed/feature.service';
import { AjaxService } from 'NewAngular/services/core/ajax.service';
import { UrlsService } from 'NewAngular/services/core/urls.service';
import { StaticFileUrlService } from 'NewAngular/services/static-file-url.service';
import * as i0 from "@angular/core";
import * as i1 from "NewAngular/services/core/ajax.service";
import * as i2 from "NewAngular/services/static-file-url.service";
import * as i3 from "NewAngular/services/core/urls.service";
import * as i4 from "EntitlementsApp/ts/features/feature-needed/feature.service";
import * as i5 from "AccountsApp/ts/services/accounts.service";
export class SecurityListMenuService {
    constructor(ajaxService, staticFileUrlService, urlsService, featureService, accountsService) {
        this.ajaxService = ajaxService;
        this.staticFileUrlService = staticFileUrlService;
        this.urlsService = urlsService;
        this.featureService = featureService;
        this.accountsService = accountsService;
        this.STATIC_MENU_URL = 'securities/securitylist_menu.json';
        this.STATIC_LABELS_URL = 'securities/securitylist_menu_labels.json';
        this.userDataMenuSetting = BOTH_DEFAULT_AND_LEGACY_DATA;
        /**
         * Filter category upsell, hide-behind-pywall, default and legacy (morningstar) entries.
         *
         * - If category has always_display as True, display it.
         * - If the user has the legacy data feature, check user's data menu setting:
         *   - If the value is both default and legacy, display the category if the user has the needed feature.
         *   - If the value is default only and the category needs the legacy data feature, hide the category.
         *   - If the value is legacy only and the category has multiple versions, hide the default version of the category.
         * - If the category has not being displayed/hidden previously, display the category if the user has the needed feature.
         *
         * @param category Category object being analyzed for filtering.
         * @return boolean True if we want to keep the category, false otherwise.
         * @private
         */
        this.filterCategory = (category) => {
            if (category.always_display)
                return true;
            const userHasUcits = this.featureService.userHasFeatures([UCITS_DATA_FEATURE]);
            const userHasEvestment = this.featureService.userHasFeatures([EVESTMENT_DATA_FEATURE]);
            const domicileVariantByFeatures = (hasUcits, hasEvestment) => {
                if (hasUcits && hasEvestment)
                    return DOMICILE_ALL;
                if (hasUcits)
                    return DOMICILE_UCITS;
                if (hasEvestment)
                    return DOMICILE_NASDAQ;
                return DOMICILE_GENERAL;
            };
            const DOMICILE_CATEGORIES = [DOMICILE_GENERAL, DOMICILE_UCITS, DOMICILE_NASDAQ, DOMICILE_ALL];
            if (DOMICILE_CATEGORIES.includes(category.name)) {
                return category.name === domicileVariantByFeatures(userHasUcits, userHasEvestment);
            }
            if (this.userHasLegacyDataFeature) {
                if (this.userDataMenuSetting === LEGACY_ONLY_DATA && category.is_default_version) {
                    return false;
                }
                if (this.userDataMenuSetting === DEFAULT_ONLY_DATA &&
                    category.features_needed?.includes(LEGACY_DATA_FEATURE)) {
                    return false;
                }
            }
            return this.featureService.userCanAccess(category);
        };
        /**
         * Remove `(Morn)` suffix from category names if the user has legacy only data menu setting.
         *
         * @param category Category object.
         * @private
         */
        this.removeSuffix = (category) => {
            if (category.name.endsWith(' (General)'))
                return { ...category, name: category.name.slice(0, -10) };
            if (category.name.endsWith(' (Nasdaq)'))
                return { ...category, name: category.name.slice(0, -9) };
            if (category.name.endsWith(' (UCITs)'))
                return { ...category, name: category.name.slice(0, -8) };
            if (category.name.endsWith(' (All)'))
                return { ...category, name: category.name.slice(0, -6) };
            if (!this.userHasLegacyDataFeature)
                return category;
            if (this.userDataMenuSetting !== LEGACY_ONLY_DATA)
                return category;
            if (category.name.endsWith(' (Morn)'))
                return { ...category, name: category.name.slice(0, -7) };
            return category;
        };
        this.userHasLegacyDataFeature = this.featureService.userHasFeatures([LEGACY_DATA_FEATURE]);
        this.accountsService.getUserPreferences().subscribe((preference) => {
            this.userDataMenuSetting = preference.data_menu_setting;
        });
        /**
         * Fetch and cache the menu and labels. Due to the use of `shareReplay`,
         * multiple subscriberes will not trigger additional HTTP requests.
         */
        this.cachedSecurityListMenu$ = this.fetchInitialMenu();
        this.cachedSecurityListLabels$ = this.fetchInitialLabels();
    }
    /**
     * Give a URL for a json file, get the absolute URL to that file and retrieve the json data.
     * @param url: string that is the url for the desired security list menu json file, e.g. 'securities/securitylist_menu.json'
     */
    getStaticData(url) {
        return this.staticFileUrlService.getAbsoluteURL(url).pipe(mergeMap((cachedUrl) => {
            return this.ajaxService.getDataObserveBody(cachedUrl, undefined, {
                'Cache-Control': 'max-age=31536000', // Cache for 1 year
            });
        }));
    }
    getDynamicMenu() {
        return this.ajaxService.getDataObserveBody(this.urlsService.SECURITY_LISTS.DYNAMIC_MENU);
    }
    getDynamicLabels() {
        return this.ajaxService.getDataObserveBody(this.urlsService.SECURITY_LISTS.DYNAMIC_LABELS);
    }
    /**
     * Creates a deep copy of the menu and sorts the categories within each security type of the SecurityListMenu.
     * @param menu SecurityListMenu to sort
     * @returns SecurityListMenu that is a copy of the param menu with categories sorted according to SECURITYLIST_MENU_ORDER_MAP
     */
    sortMenu(menu) {
        // Deep copy the menu
        const menuCopy = cloneDeep(menu);
        Object.keys(menuCopy).forEach((securityType) => {
            const categoryOrder = SECURITYLIST_MENU_ORDER_MAP[securityType] ?? [];
            menuCopy[securityType].sort((a, b) => {
                const aIndex = categoryOrder.indexOf(a.name);
                const bIndex = categoryOrder.indexOf(b.name);
                if (aIndex === -1 || bIndex === -1) {
                    return 0;
                }
                if (aIndex > bIndex) {
                    return 1;
                }
                if (bIndex > aIndex) {
                    return -1;
                }
                return 0;
            });
        });
        return menuCopy;
    }
    /**
     * Filters the categories within each security type of the SecurityListMenu.
     * @param menu SecurityListMenu to sort
     * @returns SecurityListMenu that is a copy of the param menu with categories sorted according to SECURITYLIST_MENU_ORDER_MAP
     */
    filterMenu(menu) {
        return {
            [COMPANY]: this.filterAndCleanCategories(menu[COMPANY]),
            [FUND]: this.filterAndCleanCategories(menu[FUND]),
            [INDEX]: this.filterAndCleanCategories(menu[INDEX]),
            [INDICATOR]: this.filterAndCleanCategories(menu[INDICATOR]),
            [MODEL_PORTFOLIO]: this.filterAndCleanCategories(menu[MODEL_PORTFOLIO]),
            [SECURITYLIST_GROUP_MULTI]: this.filterAndCleanCategories(menu[SECURITYLIST_GROUP_MULTI]),
        };
    }
    /**
     * Check if a list item should be included based on feature requirements.
     *
     * @param item List item object being analyzed for filtering.
     * @return boolean True if we want to keep the list item, false otherwise.
     * @private
     */
    userCanAccessListItem(item) {
        if (this.userHasLegacyDataFeature) {
            // Hide legacy items for default-only users
            if (this.userDataMenuSetting === DEFAULT_ONLY_DATA && item.features_needed?.includes(LEGACY_DATA_FEATURE)) {
                return false;
            }
        }
        return this.featureService.userCanAccess(item);
    }
    /**
     * Filter a list item and recursively filter its sub_list if it exists.
     *
     * @param item List item to filter.
     * @return Filtered list item or null if it should be excluded.
     * @private
     */
    filterListItem(item) {
        // Check if item passes feature validation
        if (!this.userCanAccessListItem(item)) {
            return null;
        }
        // Recursively filter sub_list if it exists
        if (item.sub_list && item.sub_list.length > 0) {
            const filteredSubList = this.filterListItems(item.sub_list);
            // If the sub_list is now empty and this item is just a container (key is null),
            // then hide this item entirely
            if (filteredSubList.length === 0 && item.key === null) {
                return null;
            }
            return {
                ...item,
                sub_list: filteredSubList.length > 0 ? filteredSubList : undefined,
            };
        }
        return item;
    }
    /**
     * Filter an array of list items.
     *
     * @param items Array of list items to filter.
     * @return Filtered array of list items.
     * @private
     */
    filterListItems(items) {
        if (!items || !Array.isArray(items)) {
            return [];
        }
        return items
            .map((item) => this.filterListItem(item))
            .filter((item) => item !== null);
    }
    /**
     * Filter categories and their list items, then remove empty categories.
     *
     * @param categories Array of categories to filter.
     * @return Filtered array of categories with filtered list items.
     * @private
     */
    filterAndCleanCategories(categories) {
        return categories
            .filter(this.filterCategory)
            .map((category) => {
            // Preserve categories without list_items (e.g., autocompleter-based categories)
            if (!('list_items' in category)) {
                return category;
            }
            return {
                ...category,
                list_items: this.filterListItems(category.list_items),
            };
        })
            .map(this.removeSuffix)
            .filter((category) => !category.list_items || category.list_items.length > 0); // Remove categories with empty list_items, keep those without
    }
    /**
     * Gets the security list menu data and puts the categories in the order specified by SECURITYLIST_MENU_ORDER_MAP
     */
    fetchInitialMenu() {
        // forkJoin() makes parallel requests. The next() callback is run when all requests have completed
        return forkJoin([this.getStaticData(this.STATIC_MENU_URL), this.getDynamicMenu()]).pipe(
        // shareReplay allows multiple subscribers to subscribe to this observable
        // without triggering additional HTTP requests.
        shareReplay(1), 
        // If we fail to get the static file manifest retry twice.
        retry(2), map(([staticMenu, dynamicMenu]) => {
            const staticMenuMulti = staticMenu.multi || [];
            const combinedMenu = {
                multi: staticMenuMulti.concat(dynamicMenu.multi),
                company: staticMenu.company.concat(dynamicMenu.company),
                fund: staticMenu.fund.concat(dynamicMenu.fund),
                index: staticMenu.index.concat(dynamicMenu.index),
                indicator: staticMenu.indicator.concat(dynamicMenu.indicator),
                model_portfolio: staticMenu.model_portfolio.concat(dynamicMenu.model_portfolio),
            };
            const filteredMenu = this.filterMenu(combinedMenu);
            return this.sortMenu(filteredMenu);
        }));
    }
    fetchInitialLabels() {
        // forkJoin() makes parallel requests. The next() callback is run when all requests have completed
        return forkJoin([this.getStaticData(this.STATIC_LABELS_URL), this.getDynamicLabels()]).pipe(
        // shareReplay allows multiple subscribers to subscribe to this observable
        // without triggering additional HTTP requests.
        shareReplay(1), 
        // If we fail to get the static file manifest retry twice.
        retry(2), map(([staticLabels, dynamicLabels]) => {
            // Merge static and dynamic labels
            return {
                multi: dynamicLabels.multi, // There are no static, user-generated lists
                company: Object.assign(staticLabels.company, dynamicLabels.company),
                fund: Object.assign(staticLabels.fund, dynamicLabels.fund),
                index: Object.assign(staticLabels.index, dynamicLabels.index),
                indicator: Object.assign(staticLabels.indicator, dynamicLabels.indicator),
            };
        }));
    }
    getMenu() {
        return this.cachedSecurityListMenu$;
    }
    getLabels() {
        return this.cachedSecurityListLabels$;
    }
}
SecurityListMenuService.ɵfac = function SecurityListMenuService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || SecurityListMenuService)(i0.ɵɵinject(i1.AjaxService), i0.ɵɵinject(i2.StaticFileUrlService), i0.ɵɵinject(i3.UrlsService), i0.ɵɵinject(i4.FeatureService), i0.ɵɵinject(i5.AccountsService)); };
SecurityListMenuService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: SecurityListMenuService, factory: SecurityListMenuService.ɵfac, providedIn: 'root' });
